import { useTranslation } from 'react-i18next';
import { primitiveColors, textColor } from '../../theming';
import { percentDelta, ppDelta } from '../../utils/vinExtraction';
import { useNumberFormat } from './useNumberFormat';
import NoValue from './NoValue';
import StatCard from './StatCard';
import StatDeltaLine from './StatDeltaLine';
import styles from './StatCards.module.scss';

/** The five period cards, each with its change vs the previous equal-length window (SC-QA-102: one row). */
export default function StatCards({ stats, period }) {
    const { t } = useTranslation('vinExtraction');
    const { integer, oneDecimal } = useNumberFormat();
    const { totals, previous } = stats;

    const counted = (id, color, downIsGood = false) => ({
        id,
        color,
        value: integer(totals[id]),
        delta: percentDelta(totals[id], previous[id]),
        downIsGood,
        unit: 'percent',
    });

    const cards = [
        counted('success', textColor.success),
        counted('failed', textColor.error, true),
        {
            ...counted('notFound', primitiveColors.gray400, true),
            // share of all cars checked in the range; absent from an API without it
            hint:
                totals.notFoundRate == null
                    ? undefined
                    : t('stats.cards.notFoundRate', { value: `${oneDecimal(totals.notFoundRate * 100)}%` }),
        },
        {
            id: 'successRate',
            color: textColor.link,
            // success ÷ (success + failed); Not found has its own card (SC-QA-48)
            hint: t('stats.cards.successRateHint'),
            // E10: no outcomes in the window → no rate, shown as "—" rather than 0 %
            value:
                totals.successRate === null ? (
                    <NoValue label={t('stats.noValue')} />
                ) : (
                    `${oneDecimal(totals.successRate * 100)}%`
                ),
            delta: ppDelta(totals.successRate, previous.successRate),
            downIsGood: false,
            unit: 'pp',
        },
        counted('recoveredByRetry', textColor.purple),
    ];

    return (
        <div className={styles.periodGrid}>
            {cards.map(card => (
                <StatCard
                    key={card.id}
                    id={card.id}
                    color={card.color}
                    label={t(`stats.cards.${card.id}`)}
                    hint={card.hint}
                    value={card.value}
                >
                    <StatDeltaLine delta={card.delta} downIsGood={card.downIsGood} unit={card.unit} period={period} />
                </StatCard>
            ))}
        </div>
    );
}
